import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Button,
  DocumentsList,
  LogoLoader,
  Modal,
  NewTable,
  SmartTooltip,
  StatusTags,
  TableAdv,
} from "../../../../components";
import { dateFormator, getEmployeeId, mapTableRows } from "../../../../utils";
import BulkLcaDelete from "./Lca/Modals/BulkLcaDelete";
import ShareLca from "./Lca/Modals/ShareLca";
import CreateManuval from "./Lca/AddLca/CreateManuval";
import { useDispatch, useSelector } from "react-redux";
import { getAllImmigrations } from "../../../../redux/slices/immigration/immigrationSlice";
import { usePagination } from "../../../../hooks";
import { totalCountStart } from "../../../../redux/slices/totalCount/totalCountSlice";
import { transformFiles } from "../../../../utils/transformFiles";

const Lca = ({ handleEdit }) => {
  const dispatch = useDispatch();
  const { page, limit, setPage, handleLimitChange } = usePagination(
    10,
    (newPage, newLimit) => {
      // dispatch(getAccountPayablesStart({ page: newPage, limit: newLimit }));
    },
  );

  const { value } = useSelector((state) => state.totalCount);
  const { loading, allImmigrations } = useSelector(
    (state) => state.immigration,
  );
  const isLoading = loading;
  const lcaTableData = allImmigrations?.lca || [];
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const [lcaView, setLcaView] = useState(false);
  const [isEdit, setIsEdit] = useState(true);
  //modals
  const [bulkDelete, setBulkDelete] = useState(false);
  const [shareLca, setShareLca] = useState(false);
  const [documentModal, setDocumentModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const totalCount = value?.lca?.data || 0;

  const handelDocumetsModal = (documents) => {
    setDocumentModal(!documentModal);
    setSelectedDocuments(documents);
  };
  const columns = [
    {
      key: "number",
      label: "LCA Number",
      width: "text-nowrap",
      render: (row) => (
        <span
          className="text-info cursor-pointer"
          onClick={() => {
            setSelectedEmployee(row);
            setLcaView(true);
            setIsEdit(true);
          }}
        >
          {row.number}
        </span>
      ),
    },
    {
      key: "businessUnit",
      label: "Business Unit",
      render: (row) => {
        return <div>{row?.jobDetails?.billClient?.businessName || ""}</div>;
      },
    },
    {
      key: "positionReq",
      label: "Positions",
    },
    {
      key: "employeeName",
      label: "Employee Name",
      width: "min-w-[300px]! max-w-[350px]!",
      render: (row) => {
        const value = [
          row?.employeeId?.firstName,
          row?.employeeId?.lastName,
          getEmployeeId(row?.employeeId?.uiid, row?.employeeId?.employmentType),
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <SmartTooltip
            description={value}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal
          >
            <div className="line-clamp-1! cursor-pointer">{value || "-"}</div>
          </SmartTooltip>
        );
      },
    },
    {
      key: "location",
      label: "Location",
      width: "min-w-[250px]! max-w-[300px]!",
      sortable: false,
      render: (row) => {
        const filteredData = row.placeOfEmployment
          .map(({ address }) => ({
            address: { city: address.city, state: address.state },
          }))
          .filter(({ address }) => address.city && address.state)
          .reduce((acc, current) => {
            const identifier = `${current.address.city}, ${current.address.state}`;
            if (
              !acc.some(
                ({ address }) =>
                  `${address.city}, ${address.state}` === identifier,
              )
            ) {
              acc.push(current);
            }
            return acc;
          }, []);

        const tooltipValue = filteredData
          .map((data) => `${data.address.city}, ${data.address.state}`)
          .join(" | ");

        return (
          <SmartTooltip
            description={tooltipValue}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal
          >
            <div className="line-clamp-1! cursor-pointer">
              {filteredData.map((data, index) => (
                <div key={index}>
                  {data?.address?.city} {data?.address?.state}
                </div>
              ))}
            </div>
          </SmartTooltip>
        );
      },
    },
    {
      key: "prevailingWageUSD",
      label: "Prevailing Wage (USD)",
      render: (row) => {
        return <div>{row?.salaryInfo?.wageRate || ""}</div>;
      },
    },
    {
      key: "startDate",
      label: "Start Date",
      render: (row) => dateFormator(row.startDate, "month-day-year"),
    },
    {
      key: "endDate",
      label: "End Date",
      render: (row) => dateFormator(row.endDate, "month-day-year"),
    },
    {
      key: "addedOn",
      label: "Created Date",
      render: (row) => dateFormator(row.addedOn, "month-day-year"),
    },
    {
      key: "statusData",
      label: "Status",
      render: (row) => {
        const endDate = new Date(row.endDate);
        const today = new Date();

        // Compare the endDate with today's date (set time to 00:00:00 for proper comparison)
        const isExpired = endDate < today;

        return <div>{isExpired ? "Inactive" : "Active"}</div>;
      },
    },
    {
      key: "status",
      label: "LCA Status",
      render: (row) => (
        <StatusTags
          tag={
            row.status === 1
              ? "Certified"
              : row.status === 2
                ? "Withdrawn"
                : row.status === 3
                  ? "Denied"
                  : "In Process"
          }
        />
      ),
    },
    {
      key: "comments",
      label: "Comments",
      width: "min-w-[200px]! max-w-[250px]!",
      render: (row) => {
        return (
          <SmartTooltip
            description={row?.jobDetails?.billClient?.comment}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal
          >
            <div>{row?.jobDetails?.billClient?.comment || ""}</div>
          </SmartTooltip>
        );
      },
    },
    {
      key: "documents",
      label: "Documents",
      width: "text-nowrap!",
      render: (row) => {
        const certifiedAttachments = row?.attachments?.certified || [];
        const paperAttachments = row?.attachments?.paper || [];

        const documents = transformFiles([
          ...certifiedAttachments,
          ...paperAttachments,
        ]);
        return (
          <div
            className="text-info cursor-pointer"
            onClick={
              documents.length > 0
                ? () => handelDocumetsModal(documents)
                : () => {}
            }
          >
            Show Document ({documents.length})
          </div>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-1">
          <Button
            type="icon"
            icon="delete"
            color="black"
            variant="text"
            size="xs"
          />
          <Button
            type="icon"
            variant="text"
            icon="edit_square"
            size="xs"
            color="info"
            onClick={() => handleEdit(row)}
          />
        </div>
      ),
    },
  ];
  const mappedRows = mapTableRows(lcaTableData, columns);
  useEffect(() => {
    dispatch(
      totalCountStart({
        collection: "lcas",
        coll: "LCA",
        tname: "lca",
      }),
    );
    dispatch(
      getAllImmigrations({
        coll: "LCA",
        rsn: "lca",
        limit,
        page,
        ref: "Employee Entity Paper Cert",
        incl: ["uiid firstName lastName employmentType", "businessName"],
      }),
    );
  }, [dispatch, page, limit]);
  return (
    <div className="flex flex-col overflow-hidden">
      {lcaView ? (
        // <LcaView />
        <CreateManuval
          data={selectedEmployee}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          onCancel={() => {
            setLcaView(false);
            setIsEdit(true); // reset on cancel
          }}
        />
      ) : (
        <>
          <TableAdv
            tableId={"immigraton_lca"}
            rows={mappedRows}
            columns={columns}
            showCheckbox
            onSelectionChange={setSelectedRows}
            defaultSelectedRows={selectedRows}
            serverPagination={true}
            totalCount={totalCount}
            toolbarRight={{
              search: true,
              filter: true,
              add: [
                {
                  component: (
                    <div className="flex gap-1">
                      <Button title={"Search"} size="sm" />
                      {/* <Button title={"Assign Employee"} size="sm" /> */}
                      {selectedRows.length == 1 && (
                        <>
                          <Button
                            title={"Delete"}
                            size="sm"
                            color="danger"
                            icon={"delete"}
                            onClick={() => setBulkDelete(true)}
                          />
                          <Button
                            title={"Share"}
                            size="sm"
                            color="info"
                            icon={"share"}
                            onClick={() => setShareLca(true)}
                          />
                        </>
                      )}

                      <Button
                        color="black"
                        type="icon"
                        variant="text"
                        icon={"screen_share"}
                      />
                    </div>
                  ),
                },
              ],
            }}
            page={page}
            limit={limit}
            onPaginationChange={setPage}
            onLimitChange={handleLimitChange}
          />
          <BulkLcaDelete
            isOpen={bulkDelete}
            onClose={() => setBulkDelete(false)}
          />
          <ShareLca isOpen={shareLca} onClose={() => setShareLca(false)} />
        </>
      )}
      <Modal
        isOpen={documentModal}
        onClose={() => setDocumentModal(false)}
        title="Document Details"
      >
        <DocumentsList data={selectedDocuments} />
      </Modal>
      <Backdrop isOpen={isLoading}>
        <LogoLoader />
      </Backdrop>
    </div>
  );
};

export default Lca;
